import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { Calendar, Users, TrendingUp, Eye, Star, Clock, CheckCircle, XCircle, ExternalLink, Square } from 'lucide-react';
import { Quest, QuestDetails } from '@unity-learning/shared';
import { endQuest, getQuestDetails, getQuestHistory } from '../services/api';
import toast from 'react-hot-toast';

interface AdminQuestHistoryProps {
  onViewExercise?: (exerciseId: string) => void;
}

const AdminQuestHistory: React.FC<AdminQuestHistoryProps> = ({ onViewExercise }) => {
  const [quests, setQuests] = useState<QuestDetails[]>([]);
  const [selectedQuest, setSelectedQuest] = useState<QuestDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Quête dont la fin attend un second clic, et quête en cours de fin
  const [confirmingEndId, setConfirmingEndId] = useState<string | null>(null);
  const [endingId, setEndingId] = useState<string | null>(null);

  useEffect(() => {
    fetchQuestHistory();
    // Set up polling for real-time updates
    const interval = setInterval(fetchQuestHistory, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchQuestHistory = async () => {
    try {
      setQuests(await getQuestHistory());
      setError(null);
    } catch (err) {
      console.error('Error fetching quest history:', err);
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchQuestDetails = async (questId: string) => {
    setIsLoadingDetails(true);
    try {
      setSelectedQuest(await getQuestDetails(questId));
    } catch (err) {
      console.error('Error fetching quest details:', err);
      toast.error((err as Error).message);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const handleEndQuest = async (questId: string) => {
    setEndingId(questId);
    try {
      const { message } = await endQuest(questId);
      toast.success(message || 'Quête terminée.');
      setConfirmingEndId(null);
      await fetchQuestHistory();
    } catch (err) {
      console.error('Error ending quest:', err);
      toast.error((err as Error).message || 'Impossible de terminer la quête');
    } finally {
      setEndingId(null);
    }
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status: Quest['status']) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />;
      case 'scheduled':
        return <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400" />;
      case 'expired':
        return <XCircle className="w-4 h-4 text-gray-400 dark:text-gray-500" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status: Quest['status']) => {
    const baseClasses = "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium";
    switch (status) {
      case 'active':
        return `${baseClasses} bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300`;
      case 'scheduled':
        return `${baseClasses} bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300`;
      case 'expired':
        return `${baseClasses} bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300`;
      default:
        return baseClasses;
    }
  };

  // Une quête terminée sans jamais avoir été activée est une quête programmée annulée
  const getStatusLabel = (quest: Quest) => {
    switch (quest.status) {
      case 'active':
        return 'Active';
      case 'scheduled':
        return 'Programmée';
      case 'expired':
        return quest.activatedAt ? 'Terminée' : 'Annulée';
      default:
        return quest.status;
    }
  };

  const handleViewExercise = (exerciseId: string) => {
    if (onViewExercise) {
      onViewExercise(exerciseId);
    } else {
      // Default behavior: open in new tab
      window.open(`/exercise/${exerciseId}`, '_blank');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
        <span className="ml-2 text-gray-600">Chargement de l'historique...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600 mb-4">Erreur: {error}</p>
        <button
          onClick={fetchQuestHistory}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Historique des Quêtes</h2>
        <button
          onClick={fetchQuestHistory}
          className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
        >
          Actualiser
        </button>
      </div>

      {quests.length === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <Star className="w-12 h-12 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
          <p>Aucune quête créée pour le moment</p>
        </div>
      ) : (
        <div className="space-y-4">
          {quests.map((quest) => (
            <div
              key={quest.id}
              className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <Star className="w-5 h-5 text-amber-500" />
                    <h3 className="font-medium text-gray-900 dark:text-white">
                      <ReactMarkdown components={{
                        p: ({ children }) => <span>{children}</span>,
                        strong: ({ children }) => <strong className="font-bold text-amber-600 dark:text-amber-400">{children}</strong>,
                        code: ({ children }) => <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">{children}</code>
                      }}>
                        {quest.title}
                      </ReactMarkdown>
                    </h3>
                    <div className={getStatusBadge(quest.status)}>
                      {getStatusIcon(quest.status)}
                      <span className="ml-1">{getStatusLabel(quest)}</span>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600 dark:text-gray-400">
                    <div className="flex items-center space-x-1">
                      <Calendar className="w-4 h-4" />
                      <span>Créée: {formatDate(quest.createdAt)}</span>
                    </div>

                    {quest.activatedAt && (
                      <div className="flex items-center space-x-1">
                        <CheckCircle className="w-4 h-4" />
                        <span>Activée: {formatDate(quest.activatedAt)}</span>
                      </div>
                    )}

                    {quest.scheduledAt && !quest.activatedAt && (
                      <div className="flex items-center space-x-1">
                        <Clock className="w-4 h-4" />
                        <span>{quest.status === 'scheduled' ? 'Programmée' : 'Prévue'}: {formatDate(quest.scheduledAt)}</span>
                      </div>
                    )}

                    {quest.expiredAt && quest.status === 'expired' && (
                      <div className="flex items-center space-x-1">
                        <XCircle className="w-4 h-4" />
                        <span>{quest.activatedAt ? 'Terminée' : 'Annulée'}: {formatDate(quest.expiredAt)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Quest Statistics - Inline */}
                <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400 mr-4">
                  {/* Participants */}
                  <div className="flex items-center space-x-1" title="Participants">
                    <Users className="w-4 h-4" />
                    <span className="font-medium">{quest.totalParticipants}</span>
                  </div>

                  {/* Completion Rate */}
                  <div className="flex items-center space-x-1" title="Taux de réussite">
                    <CheckCircle className="w-4 h-4" />
                    <span className="font-medium">{quest.completionRate.toFixed(1)}%</span>
                  </div>

                  {/* Average Score */}
                  <div className="flex items-center space-x-1" title="Score moyen">
                    <TrendingUp className="w-4 h-4" />
                    <span className="font-medium">{quest.averageScore.toFixed(1)}</span>
                  </div>
                </div>

                <div className="flex flex-wrap items-center justify-end gap-2">
                  {quest.status !== 'expired' && (confirmingEndId === quest.id ? (
                    <>
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {quest.status === 'scheduled' ? 'Annuler cette quête programmée ?' : 'Terminer pour toute la classe ?'}
                      </span>
                      <button
                        onClick={() => handleEndQuest(quest.id)}
                        disabled={endingId === quest.id}
                        className="inline-flex items-center px-3 py-1 text-sm bg-red-600 dark:bg-red-500 text-white rounded-md hover:bg-red-700 dark:hover:bg-red-600 disabled:opacity-50 transition-colors"
                      >
                        {endingId === quest.id ? 'En cours…' : 'Confirmer'}
                      </button>
                      <button
                        onClick={() => setConfirmingEndId(null)}
                        disabled={endingId === quest.id}
                        className="inline-flex items-center px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      >
                        Garder
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setConfirmingEndId(quest.id)}
                      title={quest.status === 'scheduled'
                        ? 'Annule la quête programmée : elle ne s\'activera pas'
                        : 'La quête disparaît de l\'accueil ; son exercice reste jouable, sans compter pour la quête'}
                      className="inline-flex items-center px-3 py-1 text-sm bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800 rounded-md hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
                    >
                      <Square className="w-4 h-4 mr-1" />
                      {quest.status === 'scheduled' ? 'Annuler' : 'Terminer'}
                    </button>
                  ))}

                  <button
                    onClick={() => handleViewExercise(quest.exerciseId)}
                    className="inline-flex items-center px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4 mr-1" />
                    Voir Exercice
                  </button>

                  <button
                    onClick={() => fetchQuestDetails(quest.id)}
                    className="inline-flex items-center px-3 py-1 text-sm bg-blue-600 dark:bg-blue-500 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    Détails
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Quest Details Modal */}
      {selectedQuest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    <ReactMarkdown components={{
                      p: ({ children }) => <span>{children}</span>,
                      strong: ({ children }) => <strong className="font-bold text-amber-600 dark:text-amber-400">{children}</strong>,
                      code: ({ children }) => <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">{children}</code>
                    }}>
                      {selectedQuest.title}
                    </ReactMarkdown>
                  </h3>
                  <div className={getStatusBadge(selectedQuest.status)}>
                    {getStatusIcon(selectedQuest.status)}
                    <span className="ml-1">{getStatusLabel(selectedQuest)}</span>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedQuest(null)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              {isLoadingDetails ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-2 text-gray-600 dark:text-gray-400">Chargement des détails...</span>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Statistics */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                      <div className="flex items-center">
                        <Users className="w-8 h-8 text-blue-600 dark:text-blue-400 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-blue-600 dark:text-blue-400">Participants</p>
                          <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">{selectedQuest.totalParticipants}</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                      <div className="flex items-center">
                        <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-green-600 dark:text-green-400">Taux de réussite</p>
                          <p className="text-2xl font-bold text-green-900 dark:text-green-100">{selectedQuest.completionRate.toFixed(1)}%</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
                      <div className="flex items-center">
                        <TrendingUp className="w-8 h-8 text-purple-600 dark:text-purple-400 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-purple-600 dark:text-purple-400">Score moyen</p>
                          <p className="text-2xl font-bold text-purple-900 dark:text-purple-100">{selectedQuest.averageScore.toFixed(1)}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Participations */}
                  <div>
                    <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                      Participations ({selectedQuest.participations.length})
                    </h4>

                    {selectedQuest.participations.length === 0 ? (
                      <p className="text-gray-500 dark:text-gray-400 text-center py-4">Aucune participation pour le moment</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                          <thead className="bg-gray-50 dark:bg-gray-700/50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Étudiant
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Score
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Tentatives
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Statut
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Date
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {selectedQuest.participations.map((participation) => (
                              <tr key={participation.userId}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                  {participation.username}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${participation.score >= 90 ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' :
                                    participation.score >= 70 ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300' :
                                      participation.score >= 50 ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300' :
                                        'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                                    }`}>
                                    {participation.score}%
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                  {participation.attemptCount || 1} tentative{(participation.attemptCount || 1) > 1 ? 's' : ''}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                  {participation.completedQuest ? (
                                    <span className="inline-flex items-center text-green-600 dark:text-green-400">
                                      <CheckCircle className="w-4 h-4 mr-1" />
                                      Terminé
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center text-gray-500 dark:text-gray-400">
                                      <XCircle className="w-4 h-4 mr-1" />
                                      En cours
                                    </span>
                                  )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                  {formatDate(participation.submittedAt)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminQuestHistory;
